import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import Link from 'next/link'
import Thumbnail from 'components/Thumbnail'
import {
  CollectiveProposalQueryResult,
  CollectiveProposalVoteFieldsFragment
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import EtherscanLink, {
  calculateShortAddress
} from '../../Common/EtherscanLink'
import Typography from 'components/common/Typography'
import { useBreakpoint } from 'hooks/useBreakpoint'

interface IProps {
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
  vote: CollectiveProposalVoteFieldsFragment
  showVote: boolean
  showSignature: boolean
}

const getEffectiveName = (vote: CollectiveProposalVoteFieldsFragment) =>
  vote.member.user.platformUser
    ? vote.member.user.platformUser.displayName
    : vote.member.user.address

function SurveyVoteListItem({
  proposal,
  vote,
  showVote,
  showSignature
}: IProps) {
  const effectiveUserName = getEffectiveName(vote)
  const { isSm } = useBreakpoint()

  const voteDetails = (
    <>
      {showVote && (
        <td className="px-3 py-3 whitespace-nowrap max-sm:w-1/2">
          {vote.surveyAnswers?.length ? (
            proposal.isShortForm ? (
              <div className="py-1.5 px-2.5">
                <Typography size="sm">
                  {vote.surveyAnswers[0].choices
                    .map(choice => choice.stringChoice || choice.dateChoice)
                    .join(', ')}
                </Typography>
              </div>
            ) : (
              <ol className="pl-6 text-sm text-gray-500 font-light">
                {vote.surveyAnswers.map((answer, i) => (
                  <li key={`answer-${i}`} className={`py-1.5 px-2.5`}>
                    {proposal.survey && proposal.survey?.totalQuestions > 1 && (
                      <Typography size="sm" color="gray">
                        Question {i + 1}:
                      </Typography>
                    )}

                    <Typography size="sm">
                      {answer.choices
                        .map(choice => choice.stringChoice || choice.dateChoice)
                        .join(', ')}
                    </Typography>
                  </li>
                ))}
              </ol>
            )
          ) : (
            <div />
          )}
        </td>
      )}

      {showSignature && (
        <td className="px-3 py-3 whitespace-nowrap text-center max-sm:w-1/2">
          {vote.signatureUrl && (
            <Link href={vote.signatureUrl} passHref target="_blank">
              <Typography
                component={'p'}
                size="sm"
                fontWeight="medium"
                color="darkGray"
              >
                {`View `}
                <FontAwesomeIcon
                  icon={faExternalLink}
                  size={'xs'}
                  className="text-gray-700"
                />
              </Typography>
            </Link>
          )}
        </td>
      )}
    </>
  )

  return (
    <tr>
      <td className="flex flex-col sm:flex-row sm:items-center py-3">
        <div className="flex space-x-2 py-1 sm:py-0 px-3">
          <Thumbnail
            src={vote.member.user.platformUser?.thumb}
            className="h-10 w-10 rounded-full overflow-hidden"
          />

          <div className="text-sm font-medium text-gray-900 mb-2 sm:mb-0">
            {effectiveUserName}{' '}
            {vote.masterVote ? (
              <Typography
                fontWeight="light"
                size="xs"
                component={'div'}
                className="break-all"
              >
                delegated vote cast by {getEffectiveName(vote.masterVote)} (
                {calculateShortAddress(vote.masterVote.member.user.address)})
              </Typography>
            ) : null}
            <EtherscanLink
              shortAddress
              type={'address'}
              address={vote.member.user.address}
            />
          </div>
        </div>

        {!isSm && (
          <table className="mt-2 w-full">
            <thead>
              <tr>
                {showVote && (
                  <th>
                    <Typography size="xs" color="gray" uppercase>
                      Answer
                    </Typography>
                  </th>
                )}

                {showSignature && (
                  <th>
                    <Typography size="xs" color="gray" uppercase>
                      Signature
                    </Typography>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              <tr>{voteDetails}</tr>
            </tbody>
          </table>
        )}
      </td>

      {isSm && voteDetails}
    </tr>
  )
}

export default SurveyVoteListItem
